// Payin fee dropdown options per tenant group.
//
// Source: middleware `source_country` export (tenant, iso3, currency, card_payment_provider,
// bank_payment_provider, card_presence_payment_provider, wallet_payment_enabled), one export per
// middleware database. Regenerate when a source country is activated or its providers change:
//   - active countries only
//   - cardProviders = card_payment_provider + card_presence_payment_provider (POS terminals)
//   - bankProviders = bank_payment_provider
//   - walletEnabled = wallet_payment_enabled
//   - the US legal entity rows are merged into the USA entry
// Last generated: 2026-09-14 from the 2026-09-14 export.
//
// Mapping to payin fee keys (what the app sends to the commissions endpoint):
//   - CRD, CRE, DEB -> cardProviders
//   - BTR           -> bankProviders (trustly, plaid, apaylo all arrive as BTR)
//   - WLT           -> "wallet" when walletEnabled
//   - TRS is never sent by the app, so it is not offered.

const payinSourceCountriesByGroup = {
  TR: {
    AUT: { name: "Austria", currency: "EUR", cardProviders: ["checkout"], bankProviders: ["trustly"], walletEnabled: true },
    BEL: { name: "Belgium", currency: "EUR", cardProviders: ["checkout"], bankProviders: [], walletEnabled: true },
    BGR: { name: "Bulgaria", currency: "EUR", cardProviders: ["checkout"], bankProviders: ["trustly"], walletEnabled: true },
    CAN: { name: "Canada", currency: "CAD", cardProviders: ["worldpay_global"], bankProviders: ["apaylo"], walletEnabled: true },
    CHE: { name: "Switzerland", currency: "CHF", cardProviders: ["checkout"], bankProviders: [], walletEnabled: true },
    CYP: { name: "Cyprus", currency: "EUR", cardProviders: ["checkout"], bankProviders: ["trustly"], walletEnabled: true },
    DEU: { name: "Germany", currency: "EUR", cardProviders: ["checkout"], bankProviders: ["trustly"], walletEnabled: true },
    DNK: { name: "Denmark", currency: "DKK", cardProviders: ["checkout"], bankProviders: ["trustly"], walletEnabled: true },
    ESP: { name: "Spain", currency: "EUR", cardProviders: ["checkout"], bankProviders: [], walletEnabled: true },
    FIN: { name: "Finland", currency: "EUR", cardProviders: ["checkout"], bankProviders: ["trustly"], walletEnabled: true },
    FRA: { name: "France", currency: "EUR", cardProviders: ["checkout"], bankProviders: [], walletEnabled: true },
    GBR: { name: "United Kingdom", currency: "GBP", cardProviders: ["checkout", "clover"], bankProviders: ["plaid"], walletEnabled: true },
    GRC: { name: "Greece", currency: "EUR", cardProviders: ["checkout"], bankProviders: ["trustly"], walletEnabled: true },
    HRV: { name: "Croatia", currency: "EUR", cardProviders: ["checkout"], bankProviders: ["trustly"], walletEnabled: true },
    IRL: { name: "Ireland", currency: "EUR", cardProviders: ["checkout"], bankProviders: [], walletEnabled: true },
    ITA: { name: "Italy", currency: "EUR", cardProviders: ["checkout"], bankProviders: [], walletEnabled: true },
    MLT: { name: "Malta", currency: "EUR", cardProviders: ["checkout"], bankProviders: [], walletEnabled: true },
    NLD: { name: "Netherlands", currency: "EUR", cardProviders: ["checkout"], bankProviders: ["trustly"], walletEnabled: true },
    NOR: { name: "Norway", currency: "NOK", cardProviders: ["checkout"], bankProviders: ["trustly"], walletEnabled: true },
    PRT: { name: "Portugal", currency: "EUR", cardProviders: ["checkout"], bankProviders: [], walletEnabled: true },
    SWE: { name: "Sweden", currency: "SEK", cardProviders: ["checkout"], bankProviders: ["trustly"], walletEnabled: true }
  },
  DHB: {
    AUT: { name: "Austria", currency: "EUR", cardProviders: ["checkout"], bankProviders: ["trustly"], walletEnabled: true },
    BEL: { name: "Belgium", currency: "EUR", cardProviders: ["checkout"], bankProviders: [], walletEnabled: true },
    BGR: { name: "Bulgaria", currency: "EUR", cardProviders: ["checkout"], bankProviders: ["trustly"], walletEnabled: true },
    CAN: { name: "Canada", currency: "CAD", cardProviders: ["worldpay_global"], bankProviders: ["apaylo"], walletEnabled: true },
    CHE: { name: "Switzerland", currency: "CHF", cardProviders: ["checkout"], bankProviders: [], walletEnabled: true },
    DEU: { name: "Germany", currency: "EUR", cardProviders: ["checkout", "nets-pos"], bankProviders: ["trustly"], walletEnabled: true },
    DNK: { name: "Denmark", currency: "DKK", cardProviders: ["checkout", "nets-pos"], bankProviders: ["trustly"], walletEnabled: true },
    ESP: { name: "Spain", currency: "EUR", cardProviders: ["checkout"], bankProviders: ["trustly"], walletEnabled: true },
    FIN: { name: "Finland", currency: "EUR", cardProviders: ["checkout"], bankProviders: ["trustly"], walletEnabled: true },
    FRA: { name: "France", currency: "EUR", cardProviders: ["checkout"], bankProviders: [], walletEnabled: true },
    GBR: { name: "United Kingdom", currency: "GBP", cardProviders: ["checkout", "clover"], bankProviders: ["plaid"], walletEnabled: true },
    GRC: { name: "Greece", currency: "EUR", cardProviders: ["checkout"], bankProviders: ["trustly"], walletEnabled: true },
    HRV: { name: "Croatia", currency: "EUR", cardProviders: ["checkout"], bankProviders: ["trustly"], walletEnabled: true },
    IRL: { name: "Ireland", currency: "EUR", cardProviders: ["checkout"], bankProviders: [], walletEnabled: true },
    ITA: { name: "Italy", currency: "EUR", cardProviders: ["checkout"], bankProviders: [], walletEnabled: true },
    MLT: { name: "Malta", currency: "EUR", cardProviders: ["checkout"], bankProviders: [], walletEnabled: false },
    NLD: { name: "Netherlands", currency: "EUR", cardProviders: ["checkout", "nets-pos"], bankProviders: ["trustly"], walletEnabled: true },
    NOR: { name: "Norway", currency: "NOK", cardProviders: ["checkout", "nets-pos"], bankProviders: ["trustly"], walletEnabled: true },
    PRT: { name: "Portugal", currency: "EUR", cardProviders: ["checkout"], bankProviders: [], walletEnabled: true },
    SWE: { name: "Sweden", currency: "SEK", cardProviders: ["checkout", "nets-pos"], bankProviders: ["trustly"], walletEnabled: true }
  },
  DHBUS: {
    USA: { name: "United States", currency: "USD", cardProviders: ["rytepay", "vantiv"], bankProviders: [], walletEnabled: false }
  }
};

// Tool tenant id -> middleware database the tenant runs on.
const payinTenantGroups = {
  p2p: "TR",
  tragents: "TR",
  dh_p2p: "DHB",
  agents: "DHB",
  usa: "DHBUS",
  usaagents: "DHBUS"
};

const cardPayInMethods = ["CRD", "CRE", "DEB"];
const walletProvider = "wallet";

const payInMethodOptions = [
  { value: "CRD", label: "Card" },
  { value: "CRE", label: "Credit Card" },
  { value: "DEB", label: "Debit Card" },
  { value: "WLT", label: "Wallet" },
  { value: "BTR", label: "Bank Transfer" },
  { value: "TRS", label: "Trustly" }
];

const paymentProviderOptions = [
  { value: "apaylo", label: "Apaylo" },
  { value: "trustly", label: "Trustly" },
  { value: "checkout", label: "Checkout" },
  { value: "clover", label: "Clover" },
  { value: "nets-pos", label: "Nets POS" },
  { value: "fiserv", label: "Fiserv" },
  { value: "vantiv", label: "Vantiv" },
  { value: "worldpay", label: "Worldpay" },
  { value: "worldpay_global", label: "Worldpay Global" },
  { value: "plaid", label: "Plaid" },
  { value: "rytepay", label: "RytePay" },
  { value: walletProvider, label: "Wallet" }
];

function countriesOf(tenantId) {
  const group = payinTenantGroups[tenantId];
  return group ? payinSourceCountriesByGroup[group] : null;
}

function hasPayinOptions(tenantId) {
  return countriesOf(tenantId) !== null;
}

function payinSourceCountries(tenantId) {
  const countries = countriesOf(tenantId) || {};
  return Object.keys(countries).map(codeIso3 => ({
    codeIso3,
    name: countries[codeIso3].name,
    currency: countries[codeIso3].currency
  }));
}

function payinCurrencyFor(tenantId, codeIso3) {
  const entry = (countriesOf(tenantId) || {})[codeIso3];
  return entry ? entry.currency : null;
}

function payinProvidersFor(tenantId, codeIso3, payInMethod) {
  const entry = (countriesOf(tenantId) || {})[codeIso3];
  if (!entry) return [];
  if (cardPayInMethods.includes(payInMethod)) return entry.cardProviders;
  if (payInMethod === "BTR") return entry.bankProviders;
  if (payInMethod === "WLT") return entry.walletEnabled ? [walletProvider] : [];
  return [];
}

function payinMethodOptionsFor(tenantId, codeIso3) {
  return payInMethodOptions.filter(
    option => payinProvidersFor(tenantId, codeIso3, option.value).length > 0
  );
}

function payinProviderOptionsFor(tenantId, codeIso3, payInMethod) {
  const providers = payinProvidersFor(tenantId, codeIso3, payInMethod);
  return paymentProviderOptions.filter(option => providers.includes(option.value));
}

export {
  payinSourceCountriesByGroup,
  payinTenantGroups,
  payInMethodOptions,
  paymentProviderOptions,
  hasPayinOptions,
  payinSourceCountries,
  payinCurrencyFor,
  payinProvidersFor,
  payinMethodOptionsFor,
  payinProviderOptionsFor
};
